import { ArrowLeft } from 'lucide-react';
import * as React from 'react';
import { Link, useParams } from 'react-router-dom';

import { usePublicOrganizerProfile } from './api';
import { PROFILE_COPY } from './constants';
import {
  NoOrganizerHere,
  OrganizerProfilePanel,
  OrganizerProfileSkeleton,
} from './organizer-profile-panel';

/**
 * The public organizer profile at `h2hspark.com/{slug}` — the **marketing** context
 * (`ORG-017` AC-3, `MEM-018` AC-6).
 *
 * ## Who reads this one
 *
 * Anyone, signed out. It is the address an organizer puts in an email, on a poster and in a
 * social bio, it is indexable, and it renders inside `MarketingLayout` — site header, footer, no
 * app chrome. `MEM-018` AC-6 is precisely this route, and a test asserts it resolves with no
 * credential.
 *
 * A signed-out visitor gets the whole profile: the band, the introduction, both event lists. What
 * they do not get is the Follow button, and they are told why in a sentence rather than
 * interrupted by a prompt — see `follow-organizer-card.tsx`.
 *
 * ## The member's route is a different one, on purpose
 *
 * `/app/organizers/{slug}` renders the same panel inside the member shell. The parity pass found
 * that a member tapping "Hosted by …" landed *here*, in marketing chrome, and "loses the tab bar
 * and the app shell". `constants.ts`'s `memberProfilePath` carries the full argument for why the
 * fix is a second route rather than one route that guesses.
 *
 * ## The width, which was the widest visible break left on the site
 *
 * This page rendered `max-w-app-canvas` — **390px**, the phone shell's token — inside a 1200px
 * marketing site, so an organizer's public address opened as a phone column with half the window
 * blank on either side. It is the member canvas now (`max-w-app-desktop`, 1180px), with the site's
 * own gutters, which is what `Web App.dc.html` §2.8 draws and what the member route already had
 * through `MemberLayout`. The panel itself sets no width: a panel that did would fight whichever
 * chrome it landed in.
 *
 * ## `ORG-017` AC-4 still holds, one layer in
 *
 * That AC requires the organizer's live preview and the public profile to render from one
 * component, and its test is a source scan for the import. `MEM-018` moved this screen's markup
 * down into `OrganizerProfilePanel`, which **both** of this profile's routes share — so the scan
 * now reads the panel rather than this file. The assertion is unchanged and the guarantee is
 * stronger: it covers the member route as well, which did not exist when it was written.
 *
 * ## Back to events goes to Discover
 *
 * Brad's design of 27 October draws the link above the header here too (`MEM-060` AC-5). This
 * address is the one on a poster or in a bio, so there is usually no page of ours to go back to,
 * and the link goes where the member route falls back to: Discover at `/app`, which is public
 * (`MEM-019` AC-1) and is the site's list of events. The marketing site has no phone bar carrying
 * a back control, so unlike the member route's link this one is drawn at every width.
 */
export function PublicOrganizerProfilePage(): React.ReactElement {
  const { slug } = useParams<{ slug: string }>();
  const query = usePublicOrganizerProfile(slug ?? '');

  if (query.isPending) {
    return (
      <div className={MARKETING_CANVAS}>
        <OrganizerProfileSkeleton />
      </div>
    );
  }
  if (query.isError || query.data === undefined) return <NoOrganizerHere />;

  const back = (
    <Link
      to="/app"
      className="mb-5 inline-flex items-center gap-1.5 font-brand text-sm font-bold text-muted-foreground transition-colors hover:text-primary"
    >
      <ArrowLeft className="h-[17px] w-[17px]" aria-hidden />
      {PROFILE_COPY.backToEvents}
    </Link>
  );

  return (
    <div className={MARKETING_CANVAS}>
      <OrganizerProfilePanel profile={query.data} backSlot={back} />
    </div>
  );
}

/**
 * The site's container for this page.
 *
 * `MarketingLayout`'s `<main>` has no padding of its own — every marketing page sets its own
 * canvas, because a landing page's bands are full-bleed and a document page's are not. This one is
 * the member app's desktop canvas so the profile is the same width at both of its addresses, with
 * the site's own 24px gutters and vertical rhythm around it.
 */
const MARKETING_CANVAS = 'mx-auto w-full max-w-app-desktop px-6 py-10 md:px-8';
